import { shiftDate, dateLabel, timeLabel } from "../dashboard/local-day.js";
import { todayIn } from "../reports/report-period.js";
import type { TaskDefinition, TaskOccurrence } from "./tasks-api.js";

export const UPCOMING_DAYS = 14;

export const weekdayLabels: Record<string, string> = {
  FR: "Fri",
  MO: "Mon",
  SA: "Sat",
  SU: "Sun",
  TH: "Thu",
  TU: "Tue",
  WE: "Wed",
};

export type TasksTab = "schedules" | "today";

export function tasksTab(value: unknown, canManage: boolean): TasksTab {
  return value === "schedules" && canManage ? "schedules" : "today";
}

// The location's current date and the last date shown in "Today & upcoming".
export function taskWindow(
  timezone: string,
  now: Date = new Date(),
  days = UPCOMING_DAYS,
): { from: string; to: string } {
  const from = todayIn(timezone, now);
  return { from, to: shiftDate(from, days) };
}

export function dayHeading(date: string, today: string): string {
  if (date === today) return "Today";
  if (date === shiftDate(today, 1)) return "Tomorrow";
  if (date === shiftDate(today, -1)) return "Yesterday";
  return dateLabel(date, "full");
}

export function groupByDate(
  items: readonly TaskOccurrence[],
): [string, TaskOccurrence[]][] {
  const grouped = new Map<string, TaskOccurrence[]>();
  for (const item of items) {
    grouped.set(item.local_date, [
      ...(grouped.get(item.local_date) ?? []),
      item,
    ]);
  }
  return [...grouped.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, entries]) => [
      date,
      [...entries].sort(
        (a, b) =>
          new Date(a.due_at).getTime() - new Date(b.due_at).getTime() ||
          a.title.localeCompare(b.title),
      ),
    ]);
}

// Today's checks, then everything after them; earlier dates stay with today.
export function splitUpcoming(
  groups: readonly [string, TaskOccurrence[]][],
  today: string,
): {
  later: [string, TaskOccurrence[]][];
  today: [string, TaskOccurrence[]][];
} {
  return {
    later: groups.filter(([date]) => date > today),
    today: groups.filter(([date]) => date <= today),
  };
}

export function nobodyAssignedLabel(count: number): string {
  return `${count} with nobody assigned`;
}

const scheduleStatusLabels: Record<TaskDefinition["status"], string> = {
  active: "Active",
  archived: "Archived",
  paused: "Paused",
};

export function scheduleStatusLabel(status: TaskDefinition["status"]): string {
  return scheduleStatusLabels[status];
}

// Only a check that needs a look gets a status word; open ones show their time.
export function showsStatus(status: TaskOccurrence["status"]): boolean {
  return !["due", "upcoming"].includes(status);
}

const statusLabels: Record<TaskOccurrence["status"], string> = {
  cancelled: "Cancelled",
  completed: "Done",
  due: "Due",
  missed: "Missed",
  skipped: "Skipped",
  upcoming: "Upcoming",
};

export function occurrenceStatusLabel(
  status: TaskOccurrence["status"],
): string {
  return statusLabels[status];
}

export function dueLabel(
  item: Pick<TaskOccurrence, "due_at">,
  timezone: string,
): string {
  return `Due ${timeLabel(item.due_at, timezone)}`;
}

export function assigneeIds(
  item: Pick<
    TaskDefinition | TaskOccurrence,
    "assignee_staff_profile_id" | "assignee_staff_profile_ids"
  >,
): string[] {
  const ids = item.assignee_staff_profile_ids;
  if (ids && ids.length > 0) return [...ids];
  return item.assignee_staff_profile_id ? [item.assignee_staff_profile_id] : [];
}

export function hasNobodyAssigned(
  item: Pick<
    TaskDefinition | TaskOccurrence,
    "assignee_staff_profile_id" | "assignee_staff_profile_ids"
  >,
): boolean {
  return assigneeIds(item).length === 0;
}

// Active schedules that no phone will show to anyone.
export function unassignedSchedules(
  definitions: readonly TaskDefinition[],
): TaskDefinition[] {
  return definitions.filter(
    (definition) =>
      definition.status === "active" && hasNobodyAssigned(definition),
  );
}

export function assigneeLabel(
  ids: readonly string[],
  names: ReadonlyMap<string, string>,
): string {
  if (ids.length === 0) return "Nobody assigned";
  const known = ids.map((id) => names.get(id)).filter(Boolean) as string[];
  if (known.length === 0)
    return `${ids.length} ${ids.length === 1 ? "person" : "people"}`;
  if (known.length <= 2) return known.join(", ");
  return `${known.slice(0, 2).join(", ")} +${known.length - 2}`;
}

export interface PersonChoice {
  id: string;
  name: string;
  status: string;
}

export function activePeople(people: readonly PersonChoice[]): PersonChoice[] {
  return people.filter((person) => person.status === "active");
}

export function toggleAssignee(
  selected: readonly string[],
  id: string,
): string[] {
  return selected.includes(id)
    ? selected.filter((entry) => entry !== id)
    : [...selected, id];
}

export function selectEveryone(
  people: readonly PersonChoice[],
  selected: readonly string[],
): string[] {
  const everyone = activePeople(people).map((person) => person.id);
  return [...new Set([...selected, ...everyone])];
}

export function everyoneSelected(
  people: readonly PersonChoice[],
  selected: readonly string[],
): boolean {
  const everyone = activePeople(people);
  return (
    everyone.length > 0 &&
    everyone.every((person) => selected.includes(person.id))
  );
}

export function recurrenceLabel(
  frequency: "daily" | "monthly" | "weekly",
  interval: number,
  detail: { monthDay?: number; weekdays?: string[] } = {},
): string {
  if (frequency === "weekly") {
    const days = (detail.weekdays ?? []).map(
      (day) => weekdayLabels[day] ?? day,
    );
    const prefix = interval > 1 ? `Every ${interval} weeks` : "Every week";
    return days.length > 0 ? `${prefix} · ${days.join(", ")}` : prefix;
  }
  if (frequency === "monthly") {
    const prefix = interval > 1 ? `Every ${interval} months` : "Every month";
    return `${prefix} · day ${detail.monthDay ?? 1}`;
  }
  return interval > 1 ? `Every ${interval} days` : "Every day";
}
